import WorkingDaySettingIcon from './WorkingDaySettingIcon'
import { determineWorkingStatus, displayTechWorkHours } from './helperFunctions'
import { useContext, useState } from 'react'
import styles from './styles.module.css'
import { ReactComponent as CopyFileIcon } from '../../../../../assets/images/copy-file-icon.svg'
import WODetailsSubModal from '../../../../../work-orders/components/work-order-detail/components/sub-modals/WODetailsSubModal'
import techAvatarIcon from '../../../../../assets/images/tech-avatar-icon.svg'
import { ToastNotificationContext } from '../../../../context/toast-context/ToastNotificationContext'
import removeDoubleCommas from '../../../../utils/removeDoubleCommas'
import { SERVICE_NAMES } from '../../../../constants/service'
import { SUB_MODAL_NAMES } from '../../../../../work-orders/components/work-order-detail/components/sub-modals/constants'
import { getAllTireRackOrderNumbers } from '../../../../utils/tire-rack-orders'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import BuildIcon from '@mui/icons-material/Build'
import { IRoute } from '../../../../../../../app/entities/Route'
import { IRouteInstruction } from '../../../../../../../app/entities/RouteInstruction'
import { TYPES } from '../../../../constants/route-instructions'

interface TechAvatarCardProps {
  route: IRoute
  techAvatarUri: string | undefined
  date: string
  triggerRerender: () => void
  blank: boolean
}

export default function TechAvatarCard(props: TechAvatarCardProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const [rideAlongModalOpen, setRideAlongModalOpen] = useState(false)
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)

  const { date, route, techAvatarUri, blank } = props
  const { technician, location, routeInstructions, secondaryTechnician } = route

  const dateToMs = new Date(date).getTime()

  async function handleCopyToClipboard() {
    let data: string[] = []
    routeInstructions.forEach((routeInstruction: IRouteInstruction) => {
      const { order, startTimeGenerated, endTimeGenerated } = routeInstruction
      if (order) {
        const { customer, installationPoint, orderServices, workOrderId } =
          order

        const orderServiceQuantity =
          orderServices?.find(
            (orderService: any) =>
              orderService.label !== SERVICE_NAMES.TIRE_DISPOSAL,
          )?.quantity ?? 0
        const allTireRackOrderNumbers = getAllTireRackOrderNumbers(order)

        const topRow = `${workOrderId} [${orderServiceQuantity}]${
          allTireRackOrderNumbers.length > 0
            ? ` ${allTireRackOrderNumbers.join(', ')}`
            : ''
        }`

        const routeDetails = [
          topRow,
          `${startTimeGenerated} - ${endTimeGenerated}`,
          customer?.fullName,
          removeDoubleCommas(installationPoint?.fullAddress ?? ''),
          customer?.email,
          customer?.trimmedPhoneNumber,
        ].join('\n')

        data.push(routeDetails)
      } else if (routeInstruction.type !== TYPES.VISIT_LOCATION) {
        const routeDetails = [
          routeInstruction.type,
          `${startTimeGenerated} - ${endTimeGenerated}`,
        ].join('\n')
        data.push(routeDetails)
      }
    })
    const textToCopy = data.join('\n\n')
    try {
      await navigator.clipboard.writeText(textToCopy)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Route copied to clipboard!',
        severity: 'info',
      })
    } catch (err) {
      console.error(err)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Route could not be copied to clipboard.',
        severity: 'error',
      })
    }
  }

  const unassignedRoute = !technician?.objectId

  let techName = technician ? technician.name : 'Unassigned'

  const styleLogic = [
    unassignedRoute ? styles.unassigned : styles.assigned,
    blank ? styles.blank : '',
  ].join(' ')

  const workingStatus = determineWorkingStatus(technician, dateToMs)

  // Day-plan rows are built on the fly and their route instructions have no
  // objectId; a ride-along can only be added once the route really exists.
  const isPersistedRoute = routeInstructions?.some((ri) => !!ri.objectId)
  const canAddRideAlong = isPersistedRoute && !unassignedRoute && !blank

  return (
    <>
      <WODetailsSubModal
        isOpen={modalOpen}
        actionName={SUB_MODAL_NAMES.TECH_ASSIGNMENT}
        handleClosed={() => setModalOpen(false)}
        route={route}
        date={props.date}
        triggerRerender={props.triggerRerender}
      />
      <WODetailsSubModal
        isOpen={rideAlongModalOpen}
        actionName={SUB_MODAL_NAMES.ADD_TECH_TO_ROUTE}
        handleClosed={() => setRideAlongModalOpen(false)}
        route={route}
        date={props.date}
        triggerRerender={props.triggerRerender}
      />
      <div className={styles.techInfoContainer}>
        <div
          className={`${styles.container} ${styleLogic}`}
          onClick={
            unassignedRoute || blank ? undefined : () => setModalOpen(true)
          }
        >
          <div>
            <img
              className={styles.techAvatar}
              src={
                techAvatarUri && !techAvatarUri?.endsWith('.png')
                  ? techAvatarUri
                  : techAvatarIcon
              }
              alt='tech avatar'
            />
            {!!technician && (
              <div className={styles.capabilityIndicators}>
                {technician.acceptsDeliveries && (
                  <div
                    className={styles.capabilityIcon}
                    data-tooltip='Technician can deliver tires'
                  >
                    <LocalShippingIcon fontSize='small' />
                  </div>
                )}
                {technician.acceptsInstallations && (
                  <div
                    className={styles.capabilityIcon}
                    data-tooltip='Technician can install tires'
                  >
                    <BuildIcon fontSize='small' />
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={{ width: '100%' }}>
            <div className={styles.name}>
              <div style={{ maxWidth: 140 }}>{techName}</div>
              <div style={{ justifySelf: 'end' }}>
                {!unassignedRoute && (
                  <WorkingDaySettingIcon {...workingStatus} />
                )}
              </div>
            </div>
            <div>{location?.name}</div>
            {secondaryTechnician && (
              <div className={styles.rideAlong}>
                + {secondaryTechnician.name} (ride-along)
              </div>
            )}
            <div style={{ width: '100%' }}>
              {!unassignedRoute && (
                <div className={styles.time}>
                  {displayTechWorkHours(technician, location, dateToMs)}
                </div>
              )}
            </div>
          </div>
        </div>
        {!blank && (
          <div className={styles.routeActions}>
            <div
              className={styles.copyRoute}
              onClick={handleCopyToClipboard}
            >
              Copy Route{' '}
              <CopyFileIcon
                style={{ height: '13px', fill: 'var(--brand-primary)' }}
              />
            </div>
            {canAddRideAlong && (
              <div
                className={styles.copyRoute}
                onClick={() => setRideAlongModalOpen(true)}
              >
                {secondaryTechnician ? 'Edit Ride-Along' : 'Add Tech to Route'}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
